import { Avatar, FlexboxGrid } from 'rsuite'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { useFeatureIsOn } from '@growthbook/growthbook-react'

import { getSmallPreview, imageLoader } from '../../utils/images'
import { ScreenSize } from '../../utils/constants'
import { Product, Supplier } from '../../utils/types/Product'
import useUrls from '../../services/useUrls'
import useIsOnMobile from '../../services/useIsOnMobile'
import { getProductHeadingText } from '../../lib/text'
import { ReviewStarsWithDisplayedAverage } from './ReviewsSection/ReviewStars'
import useReviewFetch from '../../services/useReviewFetch'
import { getAverageOfReviews } from '../../utils/reviewUtils'
import { getSalesUnitAmounts } from '../../lib/salesUnit'
import { getSupplierLogo } from '../../lib/supplier'

import styles from '../../styles/ProductSupplierHeading.module.less'

interface ProductSupplierHeadingProps {
  product: Product
  supplier?: Supplier
  handleScrollToReviews: () => void
}

const ProductSupplierHeading: React.FC<ProductSupplierHeadingProps> = (props) => {
  const { supplier, product, handleScrollToReviews } = props

  const { urlT } = useUrls()
  const { t } = useTranslation('products')
  const isOnMobile = useIsOnMobile(ScreenSize.md)
  const { ratingQuantities } = useReviewFetch(product.id)
  const sppProductHeadingV3FeatureFlag = useFeatureIsOn('spp-product-heading-v3')

  const reviewsAverage = getAverageOfReviews(ratingQuantities)
  const supplierUrl = urlT(`/supplier/${supplier?.['supplier.code']}`)
  const supplierName = supplier?.['supplier.label']
  const productHeading = supplier ? getProductHeadingText(product, supplier) : ''
  const supplierLogo = getSupplierLogo(supplier?.media)

  const getProductHeading = () => {
    if (sppProductHeadingV3FeatureFlag) {
      return getSalesUnitAmounts(product, t) === null
        ? productHeading
        : `${productHeading}, ${getSalesUnitAmounts(product, t)}`
    }

    return productHeading
  }

  return (
    <>
      <h1 className={isOnMobile ? 'h3' : 'h2'}>
        {getProductHeading()}
      </h1>

      {!!reviewsAverage && (
        <div className="margin-bottom-spacer">
          <ReviewStarsWithDisplayedAverage
            average={reviewsAverage}
            handleScrollToReviews={handleScrollToReviews}
          />
        </div>
      )}

      <FlexboxGrid
        align="middle"
        className={`flex-flow-row ${isOnMobile ? 'margin-bottom-spacer-double' : 'margin-bottom-spacer'}`}
      >
        <Link
          href={supplierUrl}
          className={styles['logo-link']}
        >
          <Avatar
            alt={`${supplierName} logo`}
            size="md"
            circle
            data-testid="supplier-logo"
            className={styles['supplier-logo']}
            src={imageLoader(getSmallPreview(supplierLogo))}
          />
        </Link>
        <p className="h5 margin-bottom-zero-important">{supplierName}</p>
        <Link
          href={supplierUrl}
          className={`flex-shrink-col-static ${styles['visit-store']}`}
        >
          {t('visit store')}
        </Link>
      </FlexboxGrid>
    </>
  )
}

export default ProductSupplierHeading
